import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, EyeOff, ArrowLeft, X, AlertCircle } from 'lucide-react';

const SignIn = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const [showError, setShowError] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!formData.password) newErrors.password = 'Password is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      setShowError(true);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  return (
    <div className="min-h-screen bg-[#0b0014]">
      {/* Error Popup */}
      {showError && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#1a1a2e] border border-red-500/30 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-400" />
                </div>
                <h3 className="text-lg font-semibold text-white">Invalid Credentials</h3>
              </div>
              <button 
                onClick={() => setShowError(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <p className="text-gray-400 text-sm mb-5">
              The email or password you entered is incorrect. Please try again.
            </p>
            <button
              onClick={() => setShowError(false)}
              className="w-full py-3 text-white font-medium bg-[#7B61FF] hover:bg-[#6B4FFF] rounded-lg transition-all"
            >
              Try Again
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#0b0014]/80 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1200px] mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_stock-mirrors/artifacts/gdgmfuzc_2.%20Logo%202.png" 
                alt="Stocks Protocol" 
                className="h-40 w-auto object-contain"
              />
            </Link>
            
            <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors flex items-center gap-1">
              <ArrowLeft size={16} />
              Back
            </Link>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="pt-28 pb-20">
        <div className="max-w-md mx-auto px-6">
          <div className="mb-10">
            <h1 className="text-3xl font-semibold text-white mb-2">
              Welcome Back
            </h1>
            <p className="text-gray-500">
              Sign in to your Stocks Protocol account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-white/[0.03] border ${errors.email ? 'border-red-500' : 'border-white/10'} rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors`}
                placeholder="john@example.com"
              />
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm text-gray-400 mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 pr-12 bg-white/[0.03] border ${errors.password ? 'border-red-500' : 'border-white/10'} rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-purple-500 transition-colors`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <a href="#" className="text-sm text-purple-400 hover:text-purple-300 transition-colors">
                Forgot password?
              </a>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full py-4 mt-2 text-white font-semibold bg-[#7B61FF] hover:bg-[#6B4FFF] rounded-lg transition-all duration-200 hover:shadow-lg hover:shadow-purple-500/25"
            >
              Sign In
            </button>

            {/* Sign Up Link */}
            <p className="text-center text-gray-500 text-sm pt-4">
              Don't have an account?{' '}
              <Link to="/signup" className="text-purple-400 hover:text-purple-300 transition-colors">Create account</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
};

export default SignIn;
